import { forwardRef, ReactNode } from "react";

import { Box, Stack, StackProps, SxProps, Theme, useTheme } from "@mui/material";

import { mergeSx } from "@/common/helpers/mergeSx";
import { FormInfoTooltip } from "@/forms-design-system/FormTooltip";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

export type FormFieldLabelProps = Omit<StackProps, "id" | "htmlFor"> & {
  id?: string;
  htmlFor?: string;
  required?: boolean;
  label: ReactNode;
  infoTooltip?: ReactNode;
  disabled?: boolean;
  size?: "small" | "medium";
  components?: {
    label?: {
      sx?: SxProps<Theme>;
    };
    infoTooltip?: {
      sx?: SxProps<Theme>;
    };
  };
};

export const FormFieldLabel = forwardRef<HTMLDivElement, FormFieldLabelProps>(
  ({ id, htmlFor, required, label, infoTooltip, disabled, size = "medium", components, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.field) {
      return <InvalidThemeErrorMessage />;
    }

    const labelStyles = mergeSx(
      {
        ...(size === "small" ? theme.typography.body2 : theme.typography.subtitle2),
        color: theme.palette.formsComponents.field.labelTextColor,
        cursor: id && !disabled ? "pointer" : undefined,
      },
      components?.label?.sx
    );

    const infoTooltipStyles = mergeSx(
      {
        position: "relative",
        top: "-2px", // For visual alignment with the label
        flexShrink: 0,
        marginInlineStart: 0.5,
        verticalAlign: "bottom",
      },
      components?.infoTooltip?.sx
    );

    return (
      <Stack ref={ref} direction="row" alignItems="flex-end" gap={0.5} {...rest}>
        <Box id={id} component="label" htmlFor={htmlFor} sx={labelStyles}>
          {label}
          {required && <span aria-hidden="true">*</span>}
          {infoTooltip && (
            <FormInfoTooltip
              color={theme.palette.formsComponents.field.labelTextColor}
              sx={infoTooltipStyles}
              size="small"
              title={infoTooltip}
            />
          )}
        </Box>
      </Stack>
    );
  }
);

FormFieldLabel.displayName = "FormFieldLabel";
